import debounce from 'lodash.debounce';
import { ConfirmationDialog } from 'uicomponents';

import { AVRoomGetters } from './getters';
import { AVMutations, PropType } from './mutations';

import type {
  AsyncPollRequestDetails,
  AVRoomState,
  FormattedChatMessage,
  MessageReaction,
  MessageReactions,
  PollAnswer,
  PollDetails,
  PollQuestion,
  RawMessage
} from './types';
import type { Broadcast, EventState } from '../event/types';

import analytics from '@/analytics';
import { AGORA, getEnv } from '@/constants';
import i18n from '@/i18n';
import DialogService from '@/services/dialogs/DialogService';
import NotificationService from '@/services/dialogs/notifications';
import {
  createP2PVideoCall,
  deletePost,
  getEventUsersBymmid,
  loadDiscussionGroupP2P,
  loadDiscussionGroups
} from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { assignMattermostUserToTeam } from '@/services/mattermostUserAssignment.service';
import { ActivityType } from '@/services/speakerActivity.service';
import {
  newPostReceived,
  textQnAPostDelete,
  textQnAReactionRecieved,
  textQnAReactionRemoved
} from '@/services/textQNA.service';
import {
  getVideoQNARequestForCurrentUserByStatus,
  postNewVideoQNARequest,
  updateVideoQNARequest
} from '@/services/videoQNA.service';
import { RootState } from '@/store';
import { makePostThrottle, scheduleRefresh } from '@/store/schedulers';
import { datesAreOnSameDay } from '@/utils/dateTime';
import { replaceRoute } from '@/utils/routerAccess';
import { stripChatInputText } from '@/utils/stringsUtil';

let schedulePostAdd: null | Function = null;
let pollsIdSetTimeout: null | NodeJS.Timeout = null;
// caps joinChannel's post-assignment retry to once per channel, avoiding unbounded 403 -> assign -> retry loops
const channelsRetriedAfterAssignment = new Set<string>();

export interface AVContext {
  state: AVRoomState;
  rootState: RootState;
  getters: AVRoomGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof AVMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, payload?: any, root?: { root: boolean }) => Promise<any>;
}

interface LoadMessagesParameters {
  noOfMessagesToLoad: number;
  referencePostID?: string; //ID of the reference post from which the new posts will be loaded.
  referenceDirection?: 'previous' | 'next'; // ID of the newest post after which the posts are to be fetched.
  replaceExistingMessages?: boolean; // whether to append the new messages to the end of the list or to replace the list.
  channel_id: string;
  no_loader?: boolean;
  fetchPinnedMessages?: boolean;
}

// Commented. Causing issue:
// https://github.com/microsoft/TypeScript/issues/15338
//
// interface LoadPostsRequestBody {
//   per_page: number;
//   before?: string;
//   after?: string;
// }

const actions = {
  toggleOnlineSocketCountPolling(context: AVContext, shouldStartPolling: boolean) {
    try {
      if (shouldStartPolling) {
        const eventID = context.rootState.route.params?.eventID;
        // Make the first call immediately
        context.dispatch('event/getActiveEventUsers', eventID, {
          root: true
        });
        if (context.state.activeBroadcastOnliner) {
          context.commit('removeActiveBroadcastOnliner');
        }
        const poller = setInterval(() => {
          context.dispatch('event/getActiveEventUsers', eventID, {
            root: true
          });
        }, 10 * 1000);
        context.commit('saveActiveBroadcastOnliner', poller);
      } else {
        context.commit('removeActiveBroadcastOnliner');
      }
    } catch (err) {
      loggerService.error('Error initializing onliner', err);
    }
  },
  joinBoothChatChannel(context: AVContext, chatChannelID: string) {
    try {
      context.commit('setChannelStatus', {
        channelId: chatChannelID,
        status: 'loading'
      });
      return context
        .dispatch('joinChatChannel', chatChannelID)
        .then(() => {
          context.commit('setChannelStatus', {
            channelId: chatChannelID,
            status: 'loaded'
          });
        })
        .catch(err => {
          loggerService.warn('Failed to join channel', chatChannelID, err);
          context.commit('setChannelStatus', {
            channelId: chatChannelID,
            status: 'left'
          });
          throw err;
        });
    } catch (err) {
      loggerService.error('error in joinBoothChatChannel', err);
    }
  },
  joinDiscussionGroupChannels(context: AVContext) {
    const room_id = context.rootState.route.params.id;
    const room = context.rootGetters['event/getDiscussionGroup'](room_id);
    if (!room) {
      loggerService.warn('Discussion Room not found, checking for alternative routes', room_id);
      const discussionGroups = context.rootState.event.discussionGroups;
      if (discussionGroups && discussionGroups.length > 0) {
        loggerService.info('Discussion groups exist, sending to discussions page');
        replaceRoute({ path: '/discussiongroups' });
      } else {
        loggerService.info('No discussion groups found, sending to live stage');
        const eventID = context.rootState.route.params.eventID;
        replaceRoute({ path: `/e/${eventID}/stage/agenda` });
      }
      return;
    }
    context.commit('setChannelStatus', {
      channelId: room.mmid,
      status: 'loading'
    });
    context
      .dispatch('joinChatChannel', room.mmid)
      .then(() => {
        context.commit('setChannelStatus', {
          channelId: room.mmid,
          status: 'loaded'
        });
      })
      .catch(err => {
        loggerService.error('Failure in joining discussiongroup chat', err);
        context.commit('setChannelStatus', {
          channelId: room.mmid,
          status: 'left'
        });
      });
  },
  joinCurrentBroadcastsChannels(context: AVContext, id?: string) {
    const event = context.rootState.event.event;
    const broadcastId = id || context.rootState.route?.params?.id;
    if (event?.agenda?.broadcasts && context.rootState.route.params?.id) {
      const broadcast = event.agenda.broadcasts.find((broadcast: Broadcast) => broadcast.id === broadcastId);
      if (broadcast) {
        context.dispatch('event/survey/loadSurveys', null, { root: true });
        context.commit('initPoll', broadcast.poll_channel_mmid);
        return context.dispatch('joinBroadcastsChannels', {
          chatChannelID: broadcast.mmid,
          pollChannelID: broadcast.poll_channel_mmid,
          textQnAChannelID: broadcast.text_qna_channel_mmid,
          speakerChatChannelID: broadcast.speaker_chat_mmid
          // liveCaptionsChannelID: broadcast.live_captions_mmid
        });
      }
      loggerService.warn('Corrupted broadcast object! Cannot rejoin the channels!');
    } else {
      loggerService.error('Not able to find broadcast for joining channels');
    }
  },
  joinCaptionChannel(context: AVContext, live_captions_mmid: string) {
    if (!live_captions_mmid) {
      loggerService.error('No live_captions_mmid was provided to join');
      return;
    }

    if (
      context.state.channel_loading[live_captions_mmid] !== 'loading' &&
      context.state.channel_loading[live_captions_mmid] !== 'loaded'
    ) {
      context.commit('setChannelStatus', {
        channelId: live_captions_mmid,
        status: 'loading'
      });
    }

    if (context.state.channel_loading[live_captions_mmid] !== 'loaded') {
      loggerService.info('Joining captions channel: ', live_captions_mmid);
      return context.dispatch('joinChannelAndHandleStates', {
        channelID: live_captions_mmid,
        channelType: 'LIVE_CAPTIONS'
      });
      // eslint-disable-next-line
    } else if (
      context.state.channel_loading[live_captions_mmid] == 'loaded' ||
      context.state.channel_loading[live_captions_mmid] == 'loading'
    ) {
      loggerService.info('Captions channel is already loaded! Fetching messages', live_captions_mmid);
      return context.dispatch('fetchInitialMessages', { channel_id: live_captions_mmid, fetchPinnedMessages: false });
    }

    loggerService.error('Failed to join captions channel');
  },
  joinBroadcastsChannels(
    context: AVContext,
    {
      chatChannelID,
      pollChannelID,
      textQnAChannelID,
      speakerChatChannelID,
      liveCaptionsChannelID
    }: {
      chatChannelID: string;
      pollChannelID: string;
      textQnAChannelID: string;
      speakerChatChannelID: string;
      liveCaptionsChannelID: string;
    }
  ) {
    const broadcastChannelsPromises: Array<Promise<any>> = [];

    return new Promise((resolve, reject) => {
      if (
        context.state.channel_loading[chatChannelID] !== 'loading' &&
        context.state.channel_loading[chatChannelID] !== 'loaded'
      ) {
        context.commit('setChannelStatus', {
          channelId: chatChannelID,
          status: 'loading'
        });
      }

      if (
        context.state.channel_loading[speakerChatChannelID] !== 'loading' &&
        context.state.channel_loading[speakerChatChannelID] !== 'loaded' &&
        context.rootGetters['event/isOrganizerOrSpeaker']
      ) {
        context.commit('setChannelStatus', {
          channelId: speakerChatChannelID,
          status: 'loading'
        });
      }
      setTimeout(() => {
        if (context.state.channel_loading[chatChannelID] !== 'loaded') {
          broadcastChannelsPromises.push(
            context
              .dispatch('joinChannelAndHandleStates', {
                channelID: chatChannelID,
                channelType: 'CHAT'
              })
              .then(() => {
                context.commit('setChannelStatus', {
                  channelId: chatChannelID,
                  status: 'loaded'
                });

                context.commit('setNumUnread', {
                  channel_id: chatChannelID,
                  res: { msg_count: 0 }
                });
              })
          );
        } else {
          broadcastChannelsPromises.push(
            context.dispatch('fetchInitialMessages', { channel_id: chatChannelID, fetchPinnedMessages: true })
          );
        }

        if (context.state.channel_loading[pollChannelID] !== 'loading') {
          context.commit('setChannelStatus', { channelId: pollChannelID, status: 'loading' });
          broadcastChannelsPromises.push(
            context.dispatch('joinChannelAndHandleStates', {
              channelID: pollChannelID,
              channelType: 'POLL'
            })
          );
        }

        if (
          context.state.channel_loading[speakerChatChannelID] !== 'loaded' &&
          context.rootGetters['event/isOrganizerOrSpeaker']
        ) {
          broadcastChannelsPromises.push(
            context
              .dispatch('joinChatChannel', speakerChatChannelID)
              .then(() => {
                context.commit('setChannelStatus', {
                  channelId: speakerChatChannelID,
                  status: 'loaded'
                });
              })
              .catch(err => {
                loggerService.warn('Failed to join speaker chat channel ', speakerChatChannelID, err);
                context.commit('setChannelStatus', {
                  channelId: speakerChatChannelID,
                  status: 'left'
                });
              })
          );
        } else if (context.rootGetters['event/isOrganizerOrSpeaker']) {
          broadcastChannelsPromises.push(
            context.dispatch('fetchInitialMessages', { channel_id: speakerChatChannelID, fetchPinnedMessages: false })
          );
        } else {
          loggerService.info(
            'mattermost: not joining speaker channel or fetching messages as user is detected as non speaker/org'
          );
        }

        if (context.state.channel_loading[textQnAChannelID] !== 'loading') {
          context.commit('setChannelStatus', { channelId: textQnAChannelID, status: 'loading' });
          broadcastChannelsPromises.push(
            context.dispatch('joinChannelAndHandleStates', {
              channelID: textQnAChannelID,
              channelType: 'TEXTQ&A'
            })
          );
        }
        Promise.all(broadcastChannelsPromises).then(resolve).catch(reject);
      }, context.state.jitterdelay * 1000);
    });
  },
  joinChannelAndHandleStates(
    context: AVContext,
    payload: {
      channelID: string;
      channelType: 'CHAT' | 'POLL' | 'TEXTQ&A' | 'LIVE_CAPTIONS';
    }
  ) {
    const currentLoadingState = context.state.channel_loading[payload?.channelID];
    if (!payload?.channelID || currentLoadingState === 'loaded') {
      return;
    }
    const channelTypeToActionMap = {
      CHAT: 'joinChatChannel',
      POLL: 'joinPollChannel',
      'TEXTQ&A': 'event/textQNA/joinTextQnaChannel',
      LIVE_CAPTIONS: 'joinLiveCaptionsChannel'
    };

    const actionToDispatch = channelTypeToActionMap[payload?.channelType];

    if (!actionToDispatch) {
      return Promise.reject(new Error('Not a valid channel type!'));
    }

    return context
      .dispatch(actionToDispatch, payload.channelID, { root: payload?.channelType === 'TEXTQ&A' })
      .then(() => {
        context.commit('setChannelStatus', {
          channelId: payload.channelID,
          status: 'loaded'
        });
      })
      .catch(err => {
        loggerService.warn(`Failed to join ${payload.channelType} channel`, payload.channelID, err);
        context.commit('setChannelStatus', {
          channelId: payload.channelID,
          status: 'left'
        });
      });
  },
  joinChannel(context: AVContext, channelid: string) {
    if (context.rootState.mattermost.joinedChannels.state === 'loading') {
      return new Promise((resolve, reject) => {
        loggerService.info('Joined channels are still loading! Retrying in 3sec');
        setTimeout(() => {
          context.dispatch('joinChannel', channelid).then(resolve).catch(reject);
        }, 3000);
      });
    }
    if (context.rootGetters['sidebar/isOverNinetyDays'] && context.rootState.event.useHideSidebar) {
      loggerService.info('on demand mode and over 90 days so not joining channels');
      return Promise.resolve([]);
    }
    const userid = context.rootState?.user?.currentUser?.mmid;
    const alreadyJoined = !!context.rootState.mattermost.joinedChannels.value[channelid];
    if (userid && !alreadyJoined) {
      loggerService.info('Joining channels again because: ', alreadyJoined);
      context.commit('setChannelStatus', {
        channelId: channelid,
        status: 'loading'
      });
      return context
        .dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `api/v4/channels/${channelid}/members`,
            method: 'POST',
            body: JSON.stringify({
              user_id: userid
            })
          },
          { root: true }
        )
        .then(res => {
          context.commit('setChannelStatus', {
            channelId: channelid,
            status: 'loaded'
          });
          context.rootState.mattermost.joinedChannels.value[channelid] = res;
          return res;
        })
        .catch(async err => {
          const statusCode = err?.statusCode ?? err?.status ?? err?.response?.status;
          if (statusCode === 403 && !channelsRetriedAfterAssignment.has(channelid)) {
            channelsRetriedAfterAssignment.add(channelid);
            const currentEvent = context.rootState.event.event;
            const assignmentResult = await assignMattermostUserToTeam({
              userId: context.rootState.user.currentUser?.id,
              teamId: currentEvent?.mmid,
              eventId: currentEvent?.id
            });

            if (assignmentResult.status === 'success') {
              return context
                .dispatch('joinChannel', channelid)
                .finally(() => channelsRetriedAfterAssignment.delete(channelid));
            }

            channelsRetriedAfterAssignment.delete(channelid);
            if (assignmentResult.status === 'blocked') {
              loggerService.warn('Mattermost user assignment is blocked; not retrying joinChannel', assignmentResult);
            } else {
              loggerService.error('Failed to create mattermost user and assign team user', assignmentResult.error);
            }
          }

          loggerService.warn('Failed to join channel', channelid, err);
          context.commit('setChannelStatus', {
            channelId: channelid,
            status: 'left'
          });
          throw err;
        });
    }

    return context.rootState.mattermost.joinedChannels.value[channelid];
  },
  joinChatChannel(context: AVContext, chatChannelID: string) {
    context.commit('initPublicChat', chatChannelID);
    return context
      .dispatch('joinChannel', chatChannelID)
      .then(() => {
        return context.dispatch('fetchInitialMessages', { channel_id: chatChannelID, fetchPinnedMessages: true });
      })
      .catch(err => {
        loggerService.warn('error in joinChatChannel', err);
        context.commit('setChannelStatus', {
          channelId: chatChannelID,
          status: 'left'
        });
        throw err;
      });
  },
  joinPollChannel(context: AVContext, pollChannelID: string) {
    context.commit('initPoll', pollChannelID);
    return context
      .dispatch('joinChannel', pollChannelID)
      .then(() => {
        return context.dispatch('fetchAllPolls', pollChannelID);
      })
      .catch(err => {
        loggerService.warn('error in joinPollChannel', err);
        context.commit('pollInitError');
        throw err;
      });
  },
  joinLiveCaptionsChannel(context: AVContext, chatChannelID: string) {
    context.commit('initPublicChat', chatChannelID);
    return context
      .dispatch('joinChannel', chatChannelID)
      .then()
      .catch(err => {
        context.commit('setChannelStatus', {
          channelId: chatChannelID,
          status: 'left'
        });
        loggerService.error('error in joinLiveCaptionsChannel', err);
        throw err;
      });
  },
  joinOfftopic(context: AVContext) {
    const offtopicChannelId = context.rootState.mattermost.publicChannels.offtopic;
    if (offtopicChannelId && context.state.channel_loading[offtopicChannelId] === 'loaded') {
      return;
    }
    return context
      .dispatch('mattermost/setPublicChannels', context.rootState.event.event, {
        root: true
      })
      .then(() => {
        context.dispatch('joinChatChannel', context.rootState.mattermost.publicChannels.offtopic).then(() => {
          context.commit('setChannelStatus', {
            channelId: context.rootState.mattermost.publicChannels.offtopic,
            status: 'loaded'
          });
        });
      })
      .catch(err => {
        loggerService.warn('Failed to join offtopic channel: ', err);
        context.commit('setChannelStatus', {
          channelId: context.rootState.mattermost.publicChannels.offtopic,
          status: 'left'
        });
      });
  },
  joinChannelsForCurrentRoute(context: AVContext) {
    if (
      ['bailed', 'bailed-noretry'].includes(context.rootState.mattermost.socketstatus) &&
      !context.rootGetters['eventsource/shouldUseSSE']
    ) {
      context.dispatch('mattermost/connect', null, { root: true });
    }
    // Always join public chat i.e. offtopic channel
    context.dispatch('joinOfftopic');

    // because EventNetwork can be open inside the broadcast context.rootState.route
    if (context.rootState.route.fullPath.includes('/broadcast')) {
      context.dispatch('joinCurrentBroadcastsChannels');
    } else if (context.rootState.route.name === 'DiscussionRoom') {
      if (!context.rootState?.event?.avroom?.isBackstageInDiscRoom) {
        context.dispatch('joinDiscussionGroupChannels');
      }
    }
    const currentBooth = context.rootGetters['mattermost/boothWithGroups'];
    if (currentBooth) {
      context.dispatch('joinBoothChatChannel', currentBooth?.mmid);
    }
  },
  joinAllBroadcastsSequentially(context: AVContext, broadcastIndex: number) {
    const broadcasts = context.rootState.event.event.agenda.broadcasts;
    if (broadcastIndex < broadcasts.length) {
      const broadcastDetails = broadcasts[broadcastIndex];
      const channels = [
        broadcastDetails.mmid,
        broadcastDetails.poll_channel_mmid,
        broadcastDetails.text_qna_channel_mmid,
        broadcastDetails.speaker_chat_mmid
        //broadcastDetails.live_captions_mmid
      ]
        .filter(chid => {
          if (chid === broadcastDetails.speaker_chat_mmid && !context.rootGetters['event/isOrganizerOrSpeaker']) {
            return false;
          }

          return !!chid;
        })
        .map(chid => {
          return context.dispatch('event/avroom/joinChannel', chid, {
            root: true
          });
        });

      Promise.allSettled(channels).then(responses => {
        for (let i = 0; i < responses.length; i++) {
          if (responses[i].status == 'fulfilled') {
            const channel: any = (responses[i] as any).value;
            const chid: string = channel.id;
            context.rootState.mattermost.joinedChannels.value[chid] = channel;
          } else {
            loggerService.info('Failed to load channel sequentially', broadcastIndex);
          }
        }
        setTimeout(() => {
          context.dispatch('joinAllBroadcastsSequentially', broadcastIndex + 1);
        }, 3000);
      });
    }
  },
  leaveChannel(context: AVContext, channelid: string) {
    const userid = (context.rootState as any).user.currentUser?.mmid;
    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/channels/${channelid}/members/${userid}`,
          method: 'DELETE',
          body: {
            user_id: userid
          },
          options: {
            keepalive: true
          }
        },
        {
          root: true
        }
      )
      .then(() => {
        delete context.rootState.mattermost.joinedChannels.value[channelid];
      })
      .catch(err => {
        loggerService.warn('Error leaving channel', err);
      });
  },
  leaveChatChannel(context: AVContext, chatChannelID: string) {
    return context.dispatch('leaveChannel', chatChannelID);
  },
  createDirectChannel(context: AVContext, user_ids: [string, string]) {
    const existingDMChat = context.state.chats.find(
      it => it.channel.name === user_ids.join('__') || it.channel.name === user_ids.reverse().join('__')
    );
    if (existingDMChat) {
      return existingDMChat.channel;
    }
    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/channels/direct`,
          method: 'POST',
          body: user_ids
        },
        {
          root: true
        }
      )
      .catch(err => {
        loggerService.warn('Error creating direct channel', err);
      });
  },
  fetchChannelPosts(
    context: AVContext,
    {
      channel_id,
      noOfMessagesToLoad,
      referencePostID,
      referenceDirection
    }: {
      channel_id: string;
      noOfMessagesToLoad?: number;
      referencePostID?: string;
      referenceDirection?: 'previous' | 'next';
    }
  ) {
    const chatRequestParams: any = {
      per_page: noOfMessagesToLoad ? noOfMessagesToLoad : 20
    };
    if (referencePostID) {
      const referenceDirectionKeyName = referenceDirection === 'previous' ? 'before' : 'after';
      chatRequestParams[referenceDirectionKeyName] = referencePostID;
    }

    chatRequestParams.skipFetchThreads = true;

    const getPostsUrl = `/api/v4/channels/${channel_id}/posts?${new URLSearchParams(chatRequestParams).toString()}`;

    return context.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: getPostsUrl,
        method: 'GET',
        query: JSON.stringify(chatRequestParams)
      },
      { root: true }
    );
  },
  fetchMessages(
    context: AVContext,
    {
      noOfMessagesToLoad,
      referencePostID,
      referenceDirection,
      replaceExistingMessages,
      channel_id,
      no_loader,
      fetchPinnedMessages
    }: LoadMessagesParameters
  ): Promise<any> {
    context.commit('initPublicChat', channel_id);
    if (!no_loader) {
      context.commit('setChannelStatus', {
        channelId: channel_id,
        status: 'loading'
      });
    }
    if (fetchPinnedMessages) {
      context.dispatch('pinnedMessages', channel_id);
    }

    return context
      .dispatch('fetchChannelPosts', {
        channel_id,
        referenceDirection,
        noOfMessagesToLoad,
        referencePostID
      })
      .then(res => {
        const p = context.dispatch('addFormattedMessages', {
          res,
          replaceExistingMessages,
          referenceDirection,
          channel_id
        });
        context.commit('setChannelStatus', {
          channelId: channel_id,
          status: 'loaded'
        });
        if (context.rootState.user.currentUser?.mmid) {
          context.dispatch('fetchNumUnread', {
            user_id: context.rootState.user.currentUser?.mmid,
            channel_id
          });
        }

        return p;
      })
      .catch(err => {
        loggerService.warn('error in fetchMessages', err);
        context.commit('setChannelStatus', {
          channelId: channel_id,
          status: 'left'
        });
      });
  },
  fetchNumUnread(context: AVContext, payload: { user_id: string; channel_id: string }) {
    const getUrl = `/api/v4/users/${payload.user_id}/channels/${payload.channel_id}/unread`;
    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: getUrl,
          method: 'GET'
        },
        {
          root: true
        }
      )
      .then(res => {
        context.commit<any>('event/avroom/setNumUnread', { channel_id: payload.channel_id, res }, { root: true });
      });
  },
  viewChannel(context: AVContext, payload: { channel_id: string }) {
    const user_id = context.rootState.user.currentUser?.mmid || '';
    const postUrl = `/api/v4/channels/members/${user_id}/view`;
    context.commit('setNumUnread', {
      channel_id: payload.channel_id,
      res: { msg_count: 0 }
    });
    return context.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: postUrl,
        method: 'POST',
        body: {
          channel_id: payload.channel_id
        }
      },
      {
        root: true
      }
    );
  },
  createAPostInChannel(
    context: AVContext,
    payload: {
      message: string;
      channel_id: string;
      props: any;
      root_id: string;
      retrycount?: number;
      askAnonymously?: boolean;
    }
  ) {
    const requestParams: any = {
      channel_id: payload.channel_id,
      message: payload.message
    };
    if (payload.props) {
      requestParams.props = payload.props;
    } else {
      requestParams.props = {};
    }

    const currentEventUser = context.rootState.user.currentEventUser;
    if (currentEventUser) {
      let name = currentEventUser.name;
      if (!name || name.trim().length === 0) {
        name = currentEventUser.email?.split('@')[0];
      }
      requestParams.identifiers = {
        name: payload.askAnonymously ? 'Anonymous' : name,
        mediumimage: payload.askAnonymously ? '' : currentEventUser.mediumimage,
        thumbnail: payload.askAnonymously ? '' : currentEventUser?.thumbnail,
        is_in_person: currentEventUser.eventUser?.is_in_person ?? false,
        coreuserid: currentEventUser.id,
        event_role: currentEventUser.event_role,
        event_user_id: currentEventUser.eventUser?.id
      };
    } else {
      if (payload.retrycount && payload.retrycount < 5) {
        loggerService.warn('No currentuser found! Will retry once!');
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            payload.retrycount = payload.retrycount || 0;
            context
              .dispatch('createAPostInChannel', {
                ...payload,
                retrycount: payload.retrycount + 1
              })
              .then(resolve)
              .catch(reject);
          }, 500);
        });
      }
      loggerService.error('Cannot post as currentUser is not defined!');
    }

    if (payload.root_id) {
      requestParams['root_id'] = payload.root_id;
    }

    const useExternal =
      isMattermostScalablityModeEnabled(context.rootState.event.event) &&
      !context.rootGetters['event/isOrganizerOrSpeaker'];
    if (useExternal) {
      return context.dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `${getEnv('VUE_APP_MM_SLOW_API')}`,
          method: 'PUT',
          body: {
            type: 'post',
            payload: {
              channel_id: payload.channel_id,
              user_id: context.rootState.user.currentEventUser?.mmid,
              message: requestParams.message,
              props: requestParams.props,
              identifiers: requestParams.identifiers
            }
          },
          configs: {
            useGivenUrl: true,
            addTokenToBodyItself: true
          }
        },
        {
          root: true
        }
      );
    }
    return context.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: `/api/v4/posts?channel_id=${payload.channel_id}`,
        method: 'POST',
        body: requestParams
      },
      { root: true }
    );
  },
  async postMessage(context: AVContext, payload: { message: string; channel_id: string; props?: any }): Promise<any> {
    const create_at = new Date();
    const user_id = context.rootState.user.currentUser?.mmid;
    const props = {
      ...payload.props,
      nonce: new Date().getTime()
    };

    const currentEventUser: any = context.rootState.user.currentEventUser ?? {};
    let name = currentEventUser.name;
    if (!name || name.trim().length === 0) {
      name = currentEventUser.email?.split('@')[0];
    }
    context.dispatch('newChatMessageReceived', {
      ...payload,
      create_at,
      user_id,
      serverResponseState: 'unverified',
      props,
      identifiers: {
        name: name,
        mediumimage: currentEventUser.mediumimage,
        is_in_person: currentEventUser.eventUser?.is_in_person ?? false,
        coreuserid: currentEventUser.id
      }
    });

    return context
      .dispatch('createAPostInChannel', {
        message: payload.message,
        channel_id: payload.channel_id,
        props
      })
      .then(() => {
        // new chat message posted should not increment unread count
        context.commit('setNumUnread', {
          channel_id: payload.channel_id,
          res: { msg_count: 0 }
        });

        context.commit('setChatLoadingState', {
          channel_id: payload.channel_id,
          status: 'loaded'
        });
      })
      .catch(err => {
        context.dispatch('newChatMessageReceived', {
          ...payload,
          create_at,
          user_id: context.rootState.user.currentUser?.mmid,
          serverResponseState: 'error',
          error: 'send_message_error',
          props,
          identifiers: {
            name: name,
            mediumimage: currentEventUser.mediumimage,
            is_in_person: currentEventUser.eventUser?.is_in_person ?? false,
            coreuserid: currentEventUser.id
          }
        });

        loggerService.error('Failed to send message ', err);
        throw err;
      });
  },
  processReactionClick(
    context: AVContext,
    payload: {
      channel_id: string;
      post_id: string;
      user_id: string;
      emoji_name: string;
      create_at: number;
    }
  ): void {
    const { post_id, user_id, emoji_name, create_at, channel_id } = payload;

    const reactions = context.getters.reactionsForPost(post_id, channel_id);

    const userHasAlreadyReacted = reactions?.[emoji_name]?.findIndex(reaction => reaction.user_id === user_id) > -1;

    if (!userHasAlreadyReacted) {
      context.dispatch('newReactionReceived', {
        reaction: {
          ...payload,
          serverResponseState: 'loading'
        },
        channelID: channel_id
      });

      let reactionPostP;
      const useExternal =
        isMattermostScalablityModeEnabled(context.rootState.event.event) &&
        !context.rootGetters['event/isOrganizerOrSpeaker'];
      if (useExternal) {
        reactionPostP = context.dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `${getEnv('VUE_APP_MM_SLOW_API')}`,
            method: 'PUT',
            body: {
              type: 'reaction_add',
              payload: {
                post_id,
                user_id,
                emoji_name,
                channel_id
              }
            },
            configs: {
              useGivenUrl: true,
              addTokenToBodyItself: true
            }
          },
          {
            root: true
          }
        );
      } else {
        reactionPostP = context.dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `/api/v4/reactions`,
            method: 'POST',
            body: { post_id, user_id, emoji_name, create_at }
          },
          { root: true }
        );
      }
      reactionPostP.catch(() => {
        context.dispatch('newReactionReceived', {
          reaction: {
            ...payload,
            serverResponseState: 'error'
          },
          channelID: channel_id
        });

        context.commit('setChatLoadingState', {
          channel_id: channel_id,
          status: 'error'
        });
      });
    } else {
      context.dispatch('reactionRemoved', {
        reaction: {
          ...payload,
          serverResponseState: 'loading'
        },
        channelID: channel_id
      });

      let reactionDelP;
      const useExternal =
        isMattermostScalablityModeEnabled(context.rootState.event.event) &&
        !context.rootGetters['event/isOrganizerOrSpeaker'];
      if (useExternal) {
        reactionDelP = context.dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `${getEnv('VUE_APP_MM_SLOW_API')}`,
            method: 'PUT',
            body: {
              type: 'reaction_delete',
              payload: {
                post_id: payload.post_id,
                user_id: payload.user_id,
                emoji_name: payload.emoji_name,
                channel_id: channel_id
              }
            },
            configs: {
              useGivenUrl: true,
              addTokenToBodyItself: true
            }
          },
          {
            root: true
          }
        );
      } else {
        reactionDelP = context.dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `/api/v4/reactions/delete`,
            method: 'POST',
            body: JSON.stringify({ user_id, post_id, emoji_name })
          },
          { root: true }
        );
      }
      reactionDelP.catch(() => {
        context.dispatch('reactionRemoved', {
          reaction: {
            ...payload,
            serverResponseState: 'error'
          },
          channelID: channel_id
        });

        context.commit('setChatLoadingState', {
          channel_id: channel_id,
          status: 'error'
        });
      });
    }
  },
  getCurrentUserDMs(context: AVContext) {
    if (context.rootState.event.event?.p2p_chat_disabled) {
      return;
    }
    const currentUser = (context.rootState as any).user.currentUser;
    if (currentUser && currentUser.mmid) {
      let channels = Promise.resolve([] as Array<{ id: string; type: 'D' | 'O'; name: string }>);
      if (context.rootState.mattermost.joinedChannels.state == 'loading') {
        if (context.rootState.mattermost.joinedChannels.loadingpromise) {
          channels = context.rootState.mattermost.joinedChannels.loadingpromise.then(() => {
            return Object.values(context.rootState.mattermost.joinedChannels.value);
          });
        } else {
          loggerService.error('Failed to find the promise for loading joined channels');
        }
      } else if (['notstarted', 'error'].includes(context.rootState.mattermost.joinedChannels.state)) {
        channels = context.dispatch('mattermost/getJoinedChannels', null, { root: true }).then(() => {
          return Object.values(context.rootState.mattermost.joinedChannels.value);
        });
      } else {
        channels = Promise.resolve(Object.values(context.rootState.mattermost.joinedChannels.value));
      }
      const dms_p = channels.then(res => {
        const offtopic = res.find((ch: any) => ch.name === 'off-topic');
        const townhall = res.find((ch: any) => ch.name === 'town-square');
        if (offtopic && offtopic.id && townhall && townhall.id) {
          context.rootState.mattermost.publicChannels.offtopic = offtopic.id;
          context.rootState.mattermost.publicChannels.townhall = townhall.id;
          loggerService.info('Joining offtopic channel');
          context
            .dispatch('event/avroom/joinChatChannel', context.rootState.mattermost.publicChannels.offtopic, {
              root: true
            })
            .catch(err => {
              loggerService.warn('Failed to join offtopic', err);
            });
        }

        if (!offtopic || !townhall) {
          loggerService.warn('Public channel not found');
          context
            .dispatch('mattermost/setPublicChannels', context.rootState.event.event, { root: true })
            .then(() => {
              loggerService.info('Joining offtopic channel after fetching public channels');
              return context.dispatch(
                'event/avroom/joinChatChannel',
                context.rootState.mattermost.publicChannels.offtopic,
                {
                  root: true
                }
              );
            })
            .catch(err => {
              loggerService.warn('Failed to join offtopic', err);
            });
        }
        const dms: Array<{
          id: string;
          type: 'D' | 'O';
          name: string;
        }> = res.filter((ch: any) => ch.type === 'D');

        // Add chats for dms
        for (const dm of dms) {
          const newchat: PropType<AVRoomState, 'chats'>[0] = {
            channel: {
              id: dm?.id,
              messages: [],
              participants: [],
              loadingState: 'loaded',
              type: dm?.type,
              name: dm?.name,
              total_unread: 0
            },
            scrolled: false,
            hasunseen: false
          };
          context.commit('addChatWithoutDock', newchat);
        }
        context.state.dmchannels = dms
          .map((it: { id: string }) => it?.id)
          .filter((id: string) => id !== 'goldbotfakechannel');

        context.dispatch('getAllUnreads', {
          usermmid: context.rootState.user.currentUser?.mmid,
          channel_ids: context.state.dmchannels
        });
        const availabletabs = context.rootGetters['sidebar/availableTabs'];
        if (availabletabs.length > 0 && availabletabs[0].id === 'messages') {
          context.dispatch('sidebar/initializeDMs', context.state.dmchannels, {
            root: true
          });
        }
        return res;
      });
      return dms_p;
    }
    loggerService.warn('Current user has no mmid');
    return Promise.reject('currentuser.mmid is not valid!');
  },
  getTownhallMessages(context: AVContext) {
    const townhall_id = context.rootState.mattermost.publicChannels.townhall;
    if (townhall_id) {
      context
        .dispatch('fetchChannelPosts', {
          channel_id: townhall_id,
          noOfMessagesToLoad: 100
        })
        .then(res => {
          const notifs = res.order
            .map((id: string) => res.posts[id])
            .map((post: any) => {
              return {
                user: { name: '' },
                salutation: '',
                body: post.message,
                time: new Date(post.create_at),
                closed: true
              };
            });
          context.commit<any>('notifications/loadAllMessages', notifs, {
            root: true
          });
        })
        .catch(err => {
          loggerService.warn('Error in fetching townhall messages', err);
        });
    }
  },
  // This user_id is the mmid
  openP2PChat(context: AVContext, user_id: string) {
    if (context.rootState.event.event?.p2p_chat_disabled) {
      return;
    }
    // Load the other user if not in store
    if (!context.rootState.event.event?.users[user_id]) {
      getEventUsersBymmid(context.rootState.event.event?.id, [user_id]).then(res => {
        context.dispatch('event/recieveUsersInBulk', res.data, {
          root: true
        });
      });
    }

    if (!context.rootState.event.stage?.showChatOnMobile) {
      context.commit<any>('event/stage/toggleMobileChat', null, { root: true });
    }
    // Get/Create a channel
    const current_user = (context.rootState as any).user.currentUser;

    // Set loading for direct chat window in 200ms
    const timerToLoader = setTimeout(() => {
      context.commit('setP2PChannelBeingCreated', true);
    }, 50);
    return context.dispatch('createDirectChannel', [user_id, current_user.mmid]).then(channel => {
      // Clear loading indicator setter
      clearInterval(timerToLoader);
      context.commit('setP2PChannelBeingCreated', false);
      context.rootState.sidebar.currentDirectChatId = channel.id;
      const chat: PropType<AVRoomState, 'chats'>[0] = {
        channel: {
          id: channel.id,
          messages: [],
          participants: [],
          loadingState: 'loaded', // deprecated. Use setChannelStatus
          type: channel.type,
          name: channel.name
        },
        scrolled: false,
        hasunseen: false
      };
      context.commit('addChat', chat);
      // Set loading
      context.commit('setChannelStatus', {
        channelID: channel.id,
        status: 'loading'
      });
      context
        .dispatch('fetchInitialMessages', { channel_id: channel.id, fetchPinnedMessages: false })
        .then(() => {
          context.commit('setChannelStatus', {
            channelID: channel.id,
            status: 'loaded'
          });
        })
        .catch(err => {
          context.commit('setChannelStatus', {
            channelID: channel.id,
            status: 'left'
          });
          loggerService.error('Error when fetching direct messages for channel: ', channel.id, err);
        });
      context.dispatch('viewChannel', { channel_id: channel.id });
    });
  },
  fetchInitialMessages(
    context: AVContext,
    { channel_id, fetchPinnedMessages }: { channel_id: string; fetchPinnedMessages: boolean }
  ) {
    return context.dispatch('initialFetcher', { channel_id, fetchPinnedMessages });
  },
  fetchInitialMessagesNoLoader(
    context: AVContext,
    { channel_id, fetchPinnedMessages }: { channel_id: string; fetchPinnedMessages: boolean }
  ) {
    return context.dispatch('initialFetcher', { channel_id, no_loader: true, fetchPinnedMessages });
  },
  initialFetcher(
    context: AVContext,
    payload: { channel_id: string; no_loader: boolean; fetchPinnedMessages: boolean }
  ) {
    return context
      .dispatch('fetchMessages', {
        replaceExistingMessages: true,
        channel_id: payload.channel_id,
        noOfMessagesToLoad: 20,
        no_loader: payload.no_loader,
        fetchPinnedMessages: payload?.fetchPinnedMessages ?? false
      })
      .catch(err => {
        loggerService.error('Error in fetching messages: ', err);
        context.commit('setChannelStatus', {
          channelId: payload.channel_id,
          status: 'loaderror'
        });
      });
  },
  addFormattedMessages(
    context: AVContext,
    {
      replaceExistingMessages,
      res,
      referenceDirection,
      channel_id
    }: {
      replaceExistingMessages: boolean;
      res: any;
      referenceDirection: 'previous' | 'next';
      channel_id: string;
    }
  ) {
    const order: string[] = res?.order && res?.order?.length ? res?.order?.slice().reverse() : [];

    const orderedFormattedMessages: FormattedChatMessage[] = getOrderedFormattedMessages(order, res.posts);

    if (replaceExistingMessages) {
      context.commit('replaceMessages', {
        posts: orderedFormattedMessages,
        channel_id
      });
    } else {
      if (referenceDirection === 'previous') {
        context.commit('addMessagesToBeginning', {
          posts: orderedFormattedMessages,
          channel_id
        });
      } else {
        context.commit('addMessages', {
          posts: orderedFormattedMessages,
          channel_id
        });
      }
    }

    // We don't need the next post ID's right now
    // context.commit("nextPostID", res.next_post_id);

    if (referenceDirection === 'previous' || replaceExistingMessages) {
      context.commit('prevPostID', { id: res?.prev_post_id, channel_id });
    }
  },
  deleteMessage(context: AVContext, msg: any) {
    try {
      const post = JSON.parse(msg.data.post);
      const { id, channel_id } = post;
      const isMessageForCurrentPollChannel = context.state.polls?.id === channel_id;
      const isTextQnaChannel = context.rootState.event.textQNA?.textQnA?.id === post.channel_id;
      if (isTextQnaChannel) {
        textQnAPostDelete(post);
      } else if (isMessageForCurrentPollChannel) {
        if (post?.props?.type === 'QUESTION') {
          context.commit('deletePollQuestionDetails', post);
        } else if (post?.props?.type === 'ANSWER') {
          context.commit('deletePollAnswer', post);
        }
      } else {
        const chat = context.state.chats.find(it => it.channel.id === channel_id);
        if (chat) {
          chat.channel.messages = chat.channel.messages.filter(it => it.id !== id);
          context.dispatch('updateReceivedMessages', post);
          chat.channel.pinned_messages = chat.channel.pinned_messages?.filter(it => it.id !== id);
        }
      }
    } catch (err) {
      loggerService.error('Error in deleting message:', err);
    }
  },
  // This just takes care of pinning/unpinning
  processMsgEdit(context: AVContext, msg: any) {
    try {
      const post = JSON.parse(msg.data.post);
      const { id, channel_id, is_pinned } = post;

      const chat = context.state.chats.find(chat => chat.channel.id === channel_id);

      const isTextQnaChannel = context.rootState.event.textQNA?.textQnA?.id === post.channel_id;

      const isPollChannel = context.state.polls?.id === post.channel_id;

      if (isPollChannel) {
        context.dispatch('processPollChannelPost', post);
        return;
      }

      if (isTextQnaChannel) {
        newPostReceived(post);
        return;
      }

      if (chat) {
        // Look for message in current messages
        const msg = chat.channel.messages.find(message => message.id === id);

        const isPostedAsQNA = !!post?.props?.qna_post_id;
        if (msg) {
          msg.is_pinned = is_pinned;
          msg.isPostedAsQNA = isPostedAsQNA;
          msg.props = { ...msg.props, qna_post_id: post?.props?.qna_post_id };
        } else {
          loggerService.info('Could not find message while trying to set is_pinned: ' + id);
        }
        // Invalidate the getter chatPinnedMessages
        context.state.chats = context.state.chats.map(chat => chat);

        // Look for message in pinned_messages
        const message = (chat.channel.pinned_messages || []).find(message => message.id === id);
        if (message && is_pinned) {
          message.is_pinned = is_pinned;
        } else if (message && !is_pinned) {
          context.commit('deletePinnedMessage', { channel_id, id });
        } else if (is_pinned) {
          const entry = msg || getFormattedChatMessage(post);
          context.commit('addPinnedMessage', { post: { ...entry, is_pinned: true }, channel_id });
        }
      } else {
        loggerService.info('Could not find chat while trying to process post_edited: ' + msg);
      }
    } catch (err) {
      loggerService.error('Error in editing messasge: ', err);
    }
  },
  addPinnedFormattedMessages(context: AVContext, payload: { channel_id: string; res: any }) {
    const res = payload.res;
    const order: string[] = res?.order && res?.order?.length ? res?.order?.slice().reverse() : [];

    const orderedFormattedMessages: FormattedChatMessage[] = getOrderedFormattedMessages(order, res.posts);

    context.commit('addPinnedMessages', {
      posts: orderedFormattedMessages,
      channel_id: payload.channel_id
    });
  },
  getOldMessages(context: AVContext, channel_id: string) {
    const chat = context.state.chats.find(chat => chat.channel.id === channel_id);
    if (chat) {
      const isLoading = context.rootState.event.avroom?.channel_loading[chat.channel.id] === 'loading';
      if (chat.channel.previousMessageID && !isLoading) {
        return context
          .dispatch('fetchMessages', {
            referencePostID: chat.channel.messages[0].id,
            referenceDirection: 'previous',
            channel_id: chat.channel.id
          })
          .catch(err => {
            loggerService.error('error in getOldMessages', err);
            // Show error loading old messages, retry
          });
      }
    }
  },
  updateReceivedMessages(
    context: AVContext,
    post: {
      channel_id: string;
    }
  ) {
    const channel_id = post.channel_id;
    const chat = context.state.chats.find(chat => chat.channel.id === channel_id);

    if (chat) {
      const debouncedRefresh = makeDebouncedMessageGetter(context, channel_id);
      debouncedRefresh();
    } else {
      loggerService.error('Received message for unopened chat');
    }
  },
  tryReceiveP2PChat(context: AVContext, newRawMessage: RawMessage & { channel_name: string }) {
    if (context.rootState.event.event?.p2p_chat_disabled) {
      return;
    }

    const channel_id = newRawMessage.channel_id;
    const channel_name = newRawMessage.channel_name;
    const chat = context.state.chats.find(chat => chat.channel.id === channel_id);

    let previousChatMessage: FormattedChatMessage | undefined;

    if (chat) {
      chat.channel.messages.forEach((message, index) => {
        if (!chat.channel.messages[index + 1]) {
          previousChatMessage = message;
        }

        if (message.props.nonce === newRawMessage?.props?.nonce) {
          previousChatMessage = chat.channel.messages[index - 1];
        }
      });
    }

    const message = getFormattedChatMessage(newRawMessage, previousChatMessage);
    if (!chat && context.state.channel_loading[channel_id] != 'loading') {
      context.commit('setChannelStatus', {
        channelId: channel_id,
        status: 'loading'
      });
      const team_id = context.rootState.event.event?.mmid;
      context
        .dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `api/v4/teams/${team_id}/channels/name/${channel_name}`,
            method: 'GET'
          },
          { root: true }
        )
        .then(channel => {
          context.commit('setChannelStatus', {
            channelId: channel_id,
            status: 'loaded'
          });
          const newchat: PropType<AVRoomState, 'chats'>[0] = {
            channel: {
              ...channel,
              messages: channel.messages || []
            },
            scrolled: false,
            hasunseen: false
          };
          context.commit('addChatWithoutDock', newchat);
          context.commit('addMessages', {
            posts: [message],
            channel_id: channel_id,
            updateUnread: context.rootState.sidebar.currentDirectChatId !== channel.id
          });

          context.dispatch('getDMUnreadsIfDmsNotInitialized', channel_id);
        })
        .catch(err => {
          loggerService.error('Error in fetching channel', channel_id, err);
          context.commit('setChannelStatus', {
            channelId: channel_id,
            status: 'loaderror'
          });
        });
    } else if (!chat && context.state.channel_loading[channel_id] == 'loading') {
      loggerService.info('There is no chat created yet but the channel is loading!');
      // Try to receive the post 3 seconds later
      if ((newRawMessage?.props?.recieveRetry ?? 0) < 5) {
        setTimeout(() => {
          if (newRawMessage) {
            (newRawMessage as any).props = newRawMessage.props || {
              recieveRetry: 0
            };
            (newRawMessage as any).props.recieveRetry += 1;
            context.dispatch('tryReceiveP2PChat', newRawMessage);
          }
        }, 3000);
      } else {
        loggerService.warn('Dropping message because channel is not becoming available!', newRawMessage);
      }
    } else {
      // Mark message as unread
      context.commit('addMessages', {
        posts: [message],
        channel_id: channel_id,
        updateUnread: context.rootState.sidebar.currentDirectChatId !== channel_id
      });
      context.dispatch('getDMUnreadsIfDmsNotInitialized', channel_id);
    }
  },
  getDMUnreadsIfDmsNotInitialized(context: AVContext, channel_id: string) {
    // This will fetch all unreads and set dmsinitialized to true if dmsinitialized is false
    // This means that dms are fetched not only on opening the dms tab, but even if the user
    // receives a dm
    // This call lets you get the unread counts of dms without actually fetching the dm channels
    if (!context.rootState.sidebar.dmsInitialized) {
      if (context.state.dmchannels && !context.state.dmchannels.includes(channel_id)) {
        context.state.dmchannels.push(channel_id);
      }
      context.dispatch('getAllUnreads', {
        usermmid: context.rootState.user.currentUser?.mmid,
        channel_ids: context.state.dmchannels
      });
    }
  },
  getAllUnreads(context: AVContext, payload: { usermmid: string; channel_ids: string[] }) {
    context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/users/${payload.usermmid}/unread`,
          method: 'POST',
          body: JSON.stringify(payload.channel_ids)
        },
        { root: true }
      )
      .then(data => {
        let response = data;
        if (!data) {
          response = [];
        }
        for (const res of response) {
          const chat = context.state.chats.find(chat => chat.channel.id === res.Channel);
          if (chat) {
            chat.channel.total_unread = parseInt(res.MsgCount);
          } else {
            const newchat: PropType<AVRoomState, 'chats'>[0] = {
              channel: {
                id: res.Channel,
                messages: [],
                participants: [],
                name: res.Name,
                loadingState: 'loaded',
                type: 'D',
                total_unread: parseInt(res.MsgCount)
              },
              scrolled: false,
              hasunseen: false
            };
            context.commit('addChatWithoutDock', newchat);
          }
        }
      })
      .catch(err => {
        loggerService.warn('Failed to fetch unreads.', err);
      });
  },
  fetchAllPolls(context: AVContext, pollChannelID: string) {
    if (!context.state.polls?.details) {
      context.commit('changePollState', 'loading');
    }
    return context
      .dispatch('fetchChannelPosts', {
        channel_id: pollChannelID,
        noOfMessagesToLoad: 400
      })
      .then((res: any) => {
        const currentUsersMMID = (context.rootState as any).user.currentUser?.mmid;
        if (context.state.polls) {
          const order: string[] = res.order && res.order.length ? res.order.slice().reverse() : [];
          const postMap: any = res.posts || {};
          const pollDetails: PollDetails = makePollDetails(order, postMap, currentUsersMMID);
          context.state.polls.details = pollDetails;
        }
        context.commit('changePollState', 'loaded');
        context.dispatch('jitteredStartPollingForPollVotes');
      })
      .catch(err => {
        loggerService.warn('error in fetchAllPolls', err);
        context.commit('pollsLoadingError');
      });
  },
  jitteredStartPollingForPollVotes(context: AVContext) {
    try {
      if (pollsIdSetTimeout !== null) {
        clearTimeout(pollsIdSetTimeout);
        pollsIdSetTimeout = null;
      }
      return;
    } catch (err) {
      loggerService.error('Error starting jittered poll votes fetcher!', err);
    }
  },
  startPollingForPollVotes(context: AVContext) {
    if (context.state.polls?.id) {
      context.dispatch('pollReactionCountsForOpenPoll');
    } else {
      loggerService.info('Could not start polling for votes', context.state.polls);
    }
  },
  pollReactionCountsForOpenPoll(context: AVContext) {
    if (pollsIdSetTimeout) {
      return;
    }

    const broadcastDetails = context.rootGetters['event/currentBroadcast'];
    if (!broadcastDetails) {
      return;
    }

    const isPollOfCurrentBroadcast = broadcastDetails?.poll_channel_mmid === context.state.polls?.id;

    if (!isPollOfCurrentBroadcast) {
      return;
    }

    const pollsDetails = context.state.polls?.details;

    if (!pollsDetails) {
      return;
    }
    if (pollsDetails?.currentOpenedPollID) {
      context.dispatch('getReactionCountsForPoll', pollsDetails?.currentOpenedPollID);
      pollsIdSetTimeout = setTimeout(
        () => {
          pollsIdSetTimeout = null;
          context.dispatch('pollReactionCountsForOpenPoll');
        },
        (context.state.polls?.timeToFetchNextPollDetails || 6) * 1000
      );
    }
  },
  getReactionCountsForPoll(context: AVContext, questionId: string) {
    try {
      const pollDetails = context.state.polls?.details;
      if (!pollDetails?.questions) {
        return;
      }

      const pollDetail = pollDetails.questions[questionId];

      if (!pollDetail?.answers) {
        return;
      }

      context
        .dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `/api/v4/posts/ids/reactioncounts`,
            method: 'POST',
            body: Object.keys(pollDetail.answers)
          },
          {
            root: true
          }
        )
        .then(answers => {
          context.commit('updateAnswerCountForPoll', {
            answersToVotesMap: answers,
            questionID: questionId,
            isAsyncFlow: false
          });
        })
        .catch(err => {
          loggerService.error('Error while fetching poll details!', err);
        });
    } catch (error) {
      loggerService.error('Error while polling for poll details!', error);
    }
  },
  incrementAsyncPollVotes(context: AVContext, pollData: Record<string, Record<string, number>>) {
    const pollDetails = context.state.polls?.details;
    const currentOpenedPollID = pollDetails?.currentOpenedPollID;
    if (!currentOpenedPollID) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(pollData, currentOpenedPollID)) {
      return;
    }

    const answersToVotesMap = pollData[currentOpenedPollID];

    context.commit('updateAnswerCountForPoll', {
      answersToVotesMap: answersToVotesMap,
      questionID: currentOpenedPollID,
      isAsyncFlow: true
    });
  },
  openPollForPolling(context: AVContext, questionID: string) {
    if (context.state.polls) {
      const pollDetails = context.state.polls.details;
      const currentOpenedPollID = pollDetails?.currentOpenedPollID;

      if (!currentOpenedPollID) {
        const currnentPollChannelID = context.state.polls.id;
        analytics.track('poll_opened', {
          eventId: context.rootState.route.params?.eventID,
          broadcastId: context.rootState.route.params?.id,
          poll_type: pollDetails?.questions?.[questionID]?.multipleChoice ? 'multiple' : 'single',
          poll_question: pollDetails?.questions?.[questionID]?.question
        });
        return context.dispatch('createAPostInChannel', {
          message: questionID,
          channel_id: currnentPollChannelID,
          props: {
            type: 'OPENED_POLL'
          }
        });
      }
    }
  },
  closeAllOpenedPolls(context: AVContext) {
    if (context.state.polls) {
      const currnentPollChannelID = context.state.polls.id;
      return context.dispatch('createAPostInChannel', {
        message: '',
        channel_id: currnentPollChannelID,
        props: {
          type: 'OPENED_POLL'
        }
      });
    }
  },
  async publishPoll(context: AVContext, questionID: string) {
    if (!context.state.polls) {
      return;
    }

    const { isOverridable, currentScreen } = context.rootGetters['sidebar/publishedStage'];
    if (!isOverridable) {
      return;
    }

    const pollDetails = context.state.polls.details;

    const currentPollChannelID = context.state.polls?.id;
    if (!currentPollChannelID) {
      loggerService.warn("Can't publish poll without a poll channel id");
      return;
    }

    if (currentScreen === 'textQna') {
      context.dispatch('sidebar/unpublish', null, { root: true });
    }

    analytics.track('poll_shared_on_stage', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id,
      poll_type: pollDetails?.questions?.[questionID]?.multipleChoice ? 'multiple' : 'single',
      poll_question: pollDetails?.questions?.[questionID]?.question
    });
    return context.dispatch('createAPostInChannel', {
      message: questionID,
      channel_id: currentPollChannelID,
      props: {
        type: 'PUBLISHED_POLL'
      }
    });
  },
  unPublishPoll(context: AVContext) {
    if (context.state.polls) {
      const currnentPollChannelID = context.state.polls?.id;
      if (currnentPollChannelID) {
        analytics.track('poll_unshared_on_stage', {
          eventId: context.rootState.route.params?.eventID,
          broadcastId: context.rootState.route.params?.id
        });
        return context.dispatch('createAPostInChannel', {
          message: '',
          channel_id: currnentPollChannelID,
          props: {
            type: 'PUBLISHED_POLL'
          }
        });
      }
    }
  },
  handleUserVote(context: AVContext, answerID: string) {
    const currentPolledQuestion = context.getters['openedPollData'];
    if (currentPolledQuestion) {
      const answerBeingVoted = currentPolledQuestion.answers[answerID];
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: true
      });
      context.commit('changePollState', 'loading');
      if (!currentPolledQuestion.multipleChoice) {
        return context
          .dispatch('handleSingleChoiceVoting', {
            answerBeingVoted,
            currentPolledQuestion
          })
          .then(() => {
            context.commit('changePollState', 'loaded');
            handleOnDemandPollSubmitted(context, currentPolledQuestion);
          })
          .catch(err => {
            NotificationService.toast(
              `${i18n.global.t('@@translate@@ Error saving vote')}`,
              `${i18n.global.t('@@translate@@ Could not save your vote at the moment. Please try again!')}`,
              'error',
              null,
              10000
            );
            loggerService.error('error in single choice handleUserVote', err);
          });
      }
      return context
        .dispatch('handleMultipleChoiceVoting', {
          answerBeingVoted,
          currentPolledQuestion
        })
        .then(() => {
          context.commit('changePollState', 'loaded');
          handleOnDemandPollSubmitted(context, currentPolledQuestion);
        })
        .catch(err => {
          NotificationService.toast(
            `${i18n.global.t('@@translate@@ Error saving vote')}`,
            `${i18n.global.t('@@translate@@ Could not save your vote at the moment. Please try again!')}`,
            'error',
            null,
            10000
          );
          loggerService.error('error in multiple choice handleUserVote', err);
        });
    }
  },
  async handleOnDemandUserVote(context: AVContext, { pollId, answerIDs }: { pollId: string; answerIDs: string[] }) {
    if (!isOnDemandMqlEnabled(context)) {
      return;
    }

    const currentPolledQuestion = context.state.polls?.details?.questions?.[pollId];
    if (!currentPolledQuestion || !answerIDs.length) {
      return;
    }

    if (context.state.polls?.details) {
      context.state.polls.details.currentOpenedPollID = pollId;
    }

    context.commit('changePollState', 'loading');
    try {
      for (const answerID of answerIDs) {
        context.commit('changePollAnswerLoadingState', {
          answerID,
          loading: true
        });
        if (!currentPolledQuestion.votedAnswers?.includes(answerID)) {
          await context.dispatch('voteForAnswer', answerID);
        }
      }
      context.commit('changePollState', 'loaded');
      handleOnDemandPollSubmitted(context, currentPolledQuestion);
    } catch (err) {
      NotificationService.toast(
        `${i18n.global.t('@@translate@@ Error saving vote')}`,
        `${i18n.global.t('@@translate@@ Could not save your vote at the moment. Please try again!')}`,
        'error',
        null,
        10000
      );
      loggerService.error('error in on-demand handleOnDemandUserVote', err);
      context.commit('changePollState', 'loaded');
    }
  },
  handleSingleChoiceVoting(
    context: AVContext,
    payload: {
      answerBeingVoted: PollAnswer;
      currentPolledQuestion: PollQuestion;
    }
  ) {
    const allVotedAnswers = payload.currentPolledQuestion.votedAnswers;
    return context.dispatch('removeExistingVotes', allVotedAnswers).then(() => {
      const wasVoted = allVotedAnswers.some(id => {
        return id === payload.answerBeingVoted.id;
      });
      if (!wasVoted) {
        return context.dispatch('voteForAnswer', payload.answerBeingVoted.id);
      }
    });
  },
  handleMultipleChoiceVoting(
    context: AVContext,
    payload: {
      answerBeingVoted: PollAnswer;
      currentPolledQuestion: PollQuestion;
    }
  ) {
    const allVotedAnswers = payload.currentPolledQuestion.votedAnswers;
    const wasVoted = allVotedAnswers.some(id => {
      return id === payload.answerBeingVoted.id;
    });
    if (wasVoted) {
      return context.dispatch('removeVoteForAnswer', payload.answerBeingVoted.id);
    }
    return context.dispatch('voteForAnswer', payload.answerBeingVoted.id);
  },
  removeExistingVotes(context: AVContext, answers: Set<string>) {
    const answerRemovePromises: any[] = [];
    answers.forEach(answerID => {
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: true
      });
      answerRemovePromises.push(context.dispatch('removeVoteForAnswer', answerID));
    });
    return Promise.all(answerRemovePromises);
  },
  publishChatMessageAsTextQnA(context: AVContext, post: any) {
    const textQnAChannel = context.rootState.event.textQNA?.textQnA;
    const requestParams: any = {
      channel_id: textQnAChannel?.id,
      moderated: textQnAChannel?.isModerated ?? false
    };

    const chat = context.state.chats.find(it => it.channel.id === post.channel_id);

    const message = chat?.channel?.messages?.find(message => message.id === post.id);

    if (!message) {
      return;
    }

    message.isMessageProcessing = true;

    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/posts/${post?.id}/migrate`,
          method: 'POST',
          body: requestParams
        },
        { root: true }
      )
      .then(() => {
        analytics.track('chat_posted_as_qna', {
          postId: post?.id,
          channelId: post?.channel_id,
          eventId: context.rootState.route.params?.eventID
        });
        message.isPostedAsQNA = true;
      })
      .catch(err => {
        loggerService.warn('Error while posting chat as qna: ', err);
        if (!message.isPostedAsQNA) {
          message.error = 'chat_qna_message_error';
        }
      })
      .finally(() => {
        message.isMessageProcessing = false;
      });
  },
  async voteForAnswer(context: AVContext, answerID: string) {
    const asyncBody = getAsyncPollPayload(context, 'reaction_add', answerID);
    const onDemand = isOnDemandMqlEnabled(context);
    if (onDemand) {
      asyncBody.on_demand = true;
    }

    try {
      await context.dispatch(
        'mattermost/makeMattermostCall',
        {
          url: getAsyncPollsUrl(),
          method: 'PUT',
          body: asyncBody,
          configs: {
            addTokenToBodyItself: true,
            useGivenUrl: true
          }
        },
        {
          root: true
        }
      );

      context.commit('addVoteForAnswerInLocalState', answerID);
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: false
      });
      return;
    } catch (error) {
      loggerService.error('Error while voting for answer: ', error);
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: false
      });
      return;
    }
  },
  async removeVoteForAnswer(context: AVContext, answerID: string) {
    const asyncRequestDetails: {
      url: string;
      method: string;
      body?: any;
      configs: any;
    } = {
      url: getAsyncPollsUrl(),
      method: 'PUT',
      configs: {
        addTokenToBodyItself: true,
        useGivenUrl: true
      }
    };

    asyncRequestDetails['body'] = getAsyncPollPayload(context, 'reaction_delete', answerID);

    try {
      await context.dispatch('mattermost/makeMattermostCall', asyncRequestDetails, {
        root: true
      });

      context.commit('removeVoteForAnswerInLocalState', answerID);
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: false
      });
      return;
    } catch (error) {
      loggerService.error('Error while removing vote for answer: ', error);
      context.commit('changePollAnswerLoadingState', {
        answerID: answerID,
        loading: false
      });
      return;
    }
  },
  newPostReceived(context: AVContext, payload: any) {
    const post = payload.post;
    const msgdata = payload.data;
    const chat = context.state.chats.find(chat => chat.channel.id === post.channel_id);
    const isChatChannel = chat && chat.channel.type !== 'D';
    const isPollChannel = context.state.polls?.id === post.channel_id;
    const isTextQnaChannel = context.rootState.event.textQNA?.textQnA?.id === post.channel_id;
    const isTownhall = context.rootState.mattermost.publicChannels.townhall === post.channel_id;

    if (isChatChannel) {
      if (!schedulePostAdd) {
        const utilfcns = makePostThrottle(600);
        schedulePostAdd = utilfcns.schedulePostAdd;
      }
      schedulePostAdd(post);
    } else if (isPollChannel) {
      context.dispatch('processPollChannelPost', post);
    } else if (isTownhall) {
      // Deprecated Townhall
    } else if (isTextQnaChannel) {
      newPostReceived(post);
    } else if (msgdata?.channel_type == 'D') {
      const channel_name = payload.data.channel_name;
      context.dispatch('tryReceiveP2PChat', { ...post, channel_name });
      scheduleRefresh(post.user_id);
    }
  },
  processPollChannelPost(context: AVContext, post: any) {
    if (!context.state.polls) {
      return;
    }

    const currentPollDetails = context.state.polls?.details;
    if (!currentPollDetails) {
      return;
    }
    const clonedPollDetails = JSON.parse(JSON.stringify(currentPollDetails));
    const { currentOpenedPollID, answerIDToQuestionIDMap, currentPublishedPollID, questions } = clonedPollDetails;
    const pollDetails = makePollDetails(
      [post.id],
      {
        [post.id]: post
      },
      (context.rootState as any).user.currentUser?.mmid,
      currentOpenedPollID,
      currentPublishedPollID,
      questions,
      answerIDToQuestionIDMap
    );

    const shouldNotify = context.state.polls?.details?.currentOpenedPollID !== post.message;
    context.commit('handleNewPollPost', post);
    context.dispatch('jitteredStartPollingForPollVotes');
    if (shouldNotify) {
      if (
        context.rootState.route.fullPath.includes('/broadcast') &&
        context.state.polls?.details &&
        post.props?.type === 'OPENED_POLL' &&
        post.message
      ) {
        context.dispatch('showNewPollNotification');
      }
    }
    context.state.polls.details = pollDetails;
  },
  showNewPollNotification(context: AVContext, payload?: { isOnDemand?: boolean }) {
    if (isOnDemandMqlEnabled(context) && !payload?.isOnDemand) {
      return;
    }

    const isBroadcastSpeakerOrOrganizer = context.rootGetters['event/isBroadcastSpeakerOrOrganizer'];
    const bodyText = isBroadcastSpeakerOrOrganizer
      ? '@@translate@@ Visit the Polls tab to view live results.'
      : '@@translate@@ A new poll is open! Visit the Polls tab to vote.';

    return DialogService.show(
      ConfirmationDialog,
      {
        header: i18n.global.t('@@translate@@ Vote for Polls'),
        bodyText: i18n.global.t(bodyText),
        confirmationBtnText: i18n.global.t('@@translate@@ Go to Polls Tab'),
        confirmationBtnVariant: 'secondary',
        cancelBtnText: i18n.global.t('@@translate@@ Close'),
        showDontAskAgain: false,
        noFixedPositioning: true
      },
      {
        placement: 'bottom-left',
        cancelOnClickOutside: true,
        autoCancelTimeout: 15000,
        hideOverlay: true,
        blockBackgroundActions: false,
        on: {
          onConfirm() {
            return Promise.resolve(true);
          },
          onDismiss() {
            return Promise.reject(false);
          }
        }
      } as any
    ).promise.then(() => {
      context.commit<any>('event/stage/toggleEngagementPanel', true, { root: true });

      const publishedSurveyId = context.rootState.event.survey?.publishedSurveyId;
      if (publishedSurveyId) {
        const survey = context.rootState.event.survey?.surveys?.[publishedSurveyId];
        if (survey?.surveyPopUpId !== null) {
          DialogService.close(survey?.surveyPopUpId);
          context.dispatch('event/survey/minimizeSurvey', publishedSurveyId, { root: true });
        }
      }

      context.commit('sidebar/changePrimaryTab' as any, 'poll', { root: true });
      if (!context.rootState.event.stage?.showChatOnMobile) {
        context.commit<any>('event/stage/toggleMobileChat', null, {
          root: true
        });
      }
    });
  },
  newChatMessageReceived(context: AVContext, post: any) {
    const chat = context.state.chats.find(it => it.channel.id === post.channel_id);

    if (!chat) {
      return;
    }

    let previousChatMessage: FormattedChatMessage | undefined;
    chat.channel.messages.forEach((message, index) => {
      if (!chat.channel.messages[index + 1]) {
        previousChatMessage = message;
      }

      if (message.props.nonce === post?.props?.nonce) {
        previousChatMessage = chat.channel.messages[index - 1];
      }
    });

    const updateUnread = true;
    context.commit('addMessages', {
      channel_id: post.channel_id,
      posts: [getFormattedChatMessage(post, previousChatMessage)],
      updateUnread
    });
    if (chat && chat.scrolled) {
      chat.hasunseen = true;
    } else if (!context.rootState.event?.event?.scalability_flags?.socket_only_chat) {
      const onfree = window.requestIdleCallback || setTimeout;
      onfree(() =>
        context.dispatch('updateReceivedMessages', {
          channel_id: post.channel_id
        })
      );
    }
  },
  showTranslatedMessage(context: AVContext, newMessage: any) {
    const lastLiveCaptionObject = context.state.lastLiveTranslatedCaption;
    const captionLanguage = <any>context?.rootState?.event?.stage?.captionLanguage;
    const currentCaptions = context.state.liveCaption;

    // code for avoide duplicated messages
    const isDuplicated =
      lastLiveCaptionObject?.start === newMessage.start && lastLiveCaptionObject?.timestamp === newMessage.timestamp;
    if (isDuplicated) {
      return;
    }

    const newCap = newMessage?.caption[captionLanguage]?.staticLine?.text || '';
    if (currentCaptions) {
      const lastCaps = currentCaptions.length > 2 ? currentCaptions?.slice(1) : currentCaptions;
      const newLiveCaptionsArray = [...lastCaps, ...[newCap]];
      context.state.liveCaption = newLiveCaptionsArray;
    } else {
      context.state.liveCaption = [newCap];
    }
    context.state.lastLiveTranslatedCaption = newMessage;
  },
  showNonTranslatedMessage(context: AVContext, newMessage: any) {
    const lastLiveCaptionObject = context.state.lastLiveCaption;
    const oldCaptions = context.state.liveCaption;

    // code for avoide duplicated messages
    const isDuplicated = lastLiveCaptionObject?.timestamp === newMessage.timestamp || newMessage.isTranslation === true;
    if (isDuplicated) {
      loggerService.info(
        'Not showing duplicate message: ',
        lastLiveCaptionObject?.timestamp,
        newMessage.timestamp,
        newMessage.isTranslation
      );
      return;
    }
    const newStaticMessage = newMessage?.caption?.staticLine?.text || '';
    const newUpdatedMessage = newMessage?.caption?.updateLine?.text || '';
    const oldStaticMessage = oldCaptions ? oldCaptions[1] || '' : '';

    let newLiveCaptionsArray;
    const newLineToAdd = lastLiveCaptionObject && lastLiveCaptionObject.start < newMessage.start;
    if (newLineToAdd) {
      newLiveCaptionsArray = [oldStaticMessage, newStaticMessage, newUpdatedMessage];
    } else if (oldCaptions) {
      newLiveCaptionsArray = [...oldCaptions];
      const captionsLength = newLiveCaptionsArray.length ? newLiveCaptionsArray.length : 1;
      newLiveCaptionsArray[captionsLength - 1] = newUpdatedMessage;
    }

    context.state.liveCaption = <Array<string>>newLiveCaptionsArray;
    context.state.lastLiveCaption = newMessage;
  },
  captionVisibilityHandler(context: AVContext) {
    context.commit('stopCaptionVisibilityInterval');
    context.state.captionVisibilityInterval = setTimeout(() => {
      context.state.liveCaption = [];
    }, 10000);
  },
  clearCaptionCache(context: AVContext) {
    context.state.lastLiveCaption = null;
    context.state.lastLiveTranslatedCaption = null;
    context.state.lastCaption = {};
    context.state.lastTranslatedCaption = {};
  },
  clearCaptions(context: AVContext) {
    context.commit('stopCaptionVisibilityInterval');
    context.state.liveCaption = [];
    context.dispatch('clearCaptionCache');
  },
  suppressCaptions(context: AVContext) {
    context.state.suppressLiveCaptions = true;
    if (context.state.suppressLiveCaptionsInterval) {
      clearInterval(context.state.suppressLiveCaptionsInterval);
    }
    context.state.suppressLiveCaptionsInterval = setTimeout(() => {
      context.state.suppressLiveCaptions = false;
    }, 9000);
  },
  updateCaptions(context: AVContext, post: any) {
    loggerService.info(`New received post ${post.channel_id}`, post);

    // condition to prevent jitter message
    if (context.state.isCaptionProcessing) {
      loggerService.info('jitter message prevented', post);
      return;
    }

    // condition to suppress captions
    if (context.state.suppressLiveCaptions) {
      loggerService.info('suppress message', post);
      return;
    }

    const newMessage = JSON.parse(post.message);

    context.dispatch('captionVisibilityHandler');

    // add message to captions
    context.dispatch('addMessageToLiveCaption', {
      post,
      newMessage
    });
    context.state.isCaptionProcessing = true;
    setTimeout(() => {
      context.state.isCaptionProcessing = false;
    }, 200);
  },
  addMessageToLiveCaption(context: AVContext, postData: any) {
    const channel_id = postData.post.channel_id;
    const newMessage = postData.newMessage;
    const isDiffCaptionsLanguageSelected = context.rootGetters['event/stage/isDiffCaptionsLanguageSelected'];

    // check if user has selected different language other than default
    if (isDiffCaptionsLanguageSelected) {
      if (newMessage.isTranslation) {
        loggerService.info(`Message for translation ${channel_id}`, postData.post);
        context.dispatch('showTranslatedMessage', newMessage);
      }
    } else {
      context.dispatch('showNonTranslatedMessage', newMessage);
    }

    // set last caption message
    if (newMessage.isTranslation) {
      loggerService.info(`Last translated caption ${channel_id}`, newMessage);
      context.state.lastTranslatedCaption = newMessage;
    } else {
      context.state.lastCaption = newMessage;
    }
  },
  switchCaptionLanguage(context: AVContext) {
    context.state.liveCaption = [];
    context.state.lastLiveCaption = null;
    context.state.lastLiveTranslatedCaption = null;

    // check if different language is selected or not
    const isDiffCaptionsLanguageSelected = context.rootGetters['event/stage/isDiffCaptionsLanguageSelected'];
    if (isDiffCaptionsLanguageSelected) {
      context.dispatch('showTranslatedMessage', context.state.lastTranslatedCaption);
    } else {
      context.dispatch('showNonTranslatedMessage', context.state.lastCaption);
    }
  },
  newReactionReceived(
    context: AVContext,
    {
      reaction,
      channelID
    }: {
      reaction: MessageReaction;
      channelID: string;
    }
  ) {
    try {
      const isPollChannel = context.state.polls?.id === channelID;
      const isTextQnaChannel = context.rootState.event.textQNA?.textQnA?.id === channelID;

      if (isTextQnaChannel) {
        textQnAReactionRecieved(reaction);
      } else if (!isPollChannel) {
        context.commit('addReaction', {
          reaction,
          channel_id: channelID
        });
      }
    } catch (err) {
      loggerService.error('Failure while trying to receive reaction', err);
    }
  },
  reactionRemoved(
    context: AVContext,
    {
      reaction,
      channelID
    }: {
      reaction: any;
      channelID: string;
    }
  ) {
    try {
      const isPollChannel = context.state.polls?.id === channelID;
      const isTextQnaChannel = context.rootState.event.textQNA?.textQnA?.id === channelID;

      if (isTextQnaChannel) {
        textQnAReactionRemoved(reaction);
      } else if (!isPollChannel) {
        context.commit('deleteReaction', {
          reaction,
          channel_id: channelID
        });
      }
    } catch (err) {
      loggerService.error('error in reactionRemoved', err);
    }
  },
  fetchPostDetails(context: AVContext, postID: string) {
    return context.dispatch(
      'mattermost/makeMattermostCall',
      {
        url: `/api/v4/posts/${postID}`,
        method: 'GET'
      },
      {
        root: true
      }
    );
  },

  // TODO: Remove this  @Pankaj
  remakeAvroomStateWithUsers(context: AVContext) {
    // if (context.state.scheduleTextQARefresh) {
    //   context.state.scheduleTextQARefresh = false;
    //   context.dispatch("updateTextQADetails");
    // }
  },
  raiseVideoQNARequest(context: AVContext) {
    const broadcastId = context.rootState.route.params.id.toString();

    context.dispatch('createVideoQNAStream').then(() => {
      postNewVideoQNARequest(broadcastId);
    });
  },
  acceptVideoQNARequest(context: AVContext, requestId: string) {
    try {
      return updateVideoQNARequest(requestId, 'ACCEPTED');
    } catch (error) {
      loggerService.captureException(error as Error);
    }
  },
  rejectVideoQNARequest(context: AVContext, requestId: string) {
    try {
      return updateVideoQNARequest(requestId, 'REJECTED');
    } catch (error) {
      loggerService.captureException(error as Error);
    }
  },
  completeVideoQNARequest(context: AVContext, requestId: string) {
    try {
      return updateVideoQNARequest(requestId, 'COMPLETED');
    } catch (error) {
      loggerService.captureException(error as Error);
    }
  },
  cancelVideoQNARequestOrStage(context: AVContext, broadcast: Broadcast) {
    context.dispatch('cancelVideoQNARequest', broadcast);
    context.dispatch('attendeeLeaveVideoQNAStage', broadcast);
  },
  cancelVideoQNARequest(context: AVContext, broadcast: Broadcast) {
    const openRequest = getVideoQNARequestForCurrentUserByStatus(broadcast, 'OPEN');

    if (!openRequest) {
      return;
    }

    updateVideoQNARequest(openRequest.id, 'CANCELLED');

    context.dispatch(
      'mediaroom/changeUserRoleInRoom',
      {
        channelName: broadcast.id,
        userRole: AGORA.ROLE.ATTENDEE
      },
      { root: true }
    );
  },
  attendeeLeaveVideoQNAStage(context: AVContext, broadcast: Broadcast) {
    const acceptedRequest = getVideoQNARequestForCurrentUserByStatus(broadcast, 'ACCEPTED');

    if (!acceptedRequest) {
      return;
    }

    updateVideoQNARequest(acceptedRequest.id, 'COMPLETED');
  },
  createVideoQNAStream(context: AVContext) {
    context.dispatch(
      'mediaroom/createLocalAudioVideoTracks',
      {
        channelName: context.rootState.route.params.id
      },
      { root: true }
    );
  },
  publishVideoQNAStream(context: AVContext) {
    const hasLocalStream = context.rootState.mediaroom.localTracks.length;
    let promise = Promise.resolve();

    if (!hasLocalStream) {
      promise = context.dispatch('createVideoQNAStream');
    }

    promise
      .then(() =>
        context.dispatch(
          'mediaroom/changeUserRoleInRoom',
          {
            channelName: context.rootState.route.params.id,
            userRole: AGORA.ROLE.SPEAKER
          },
          { root: true }
        )
      )
      .then(() => {
        analytics.track('video_qna_stream_publish');
      })
      .catch(error => {
        analytics.track('video_qna_stream_publish_error', {
          error
        });
        loggerService.captureException(error as Error);
      });
  },
  async callAPeer(context: AVContext, peerEventUserId: any) {
    try {
      const response: any = await createP2PVideoCall(peerEventUserId);
      const { id: roomId, owner: whoIsVideoCallingId } = response.data;
      return {
        roomId,
        whoIsVideoCallingId
      };
    } catch (e) {
      loggerService.error('Error in callAPeer', e);
      return null;
    }
  },
  receiveIncomingVideoCallMessage(
    context: AVContext,
    receivedPayload: {
      roomId: string;
      channel_id: string;
      whoIsVideoCallingId: string;
    } | null
  ) {
    try {
      const currentChatChannelId = context.rootGetters['sidebar/currentChat']?.channel.id;
      const currentUserId = context.rootGetters['getCurrentUserId'];
      if (receivedPayload?.channel_id === currentChatChannelId) {
        const callingUser = context.rootGetters['event/userById'](receivedPayload?.whoIsVideoCallingId);
        context.dispatch('setIncomingVideoCall', {
          roomId: receivedPayload?.roomId,
          callingUser
        });
      }
      if (currentUserId === receivedPayload?.whoIsVideoCallingId) {
        const privateRoomId = context.rootGetters['getPrivateDiscussionRoom'];
        if (!privateRoomId || !privateRoomId?.id) {
          context.dispatch('setPrivateDiscussionRoom', { id: receivedPayload?.roomId }, { root: true });
        }
      }
    } catch (e) {
      loggerService.error('Received unparsable breakout payload: ' + receivedPayload?.channel_id);
    }
  },
  setIncomingVideoCall(context: AVContext, payload: { roomId: string; callingUser: any } | null) {
    context.commit('setIncomingVideoCall', payload);
  },
  addToAttendeeDiscussionRooms(context: AVContext, payload: { userId: string; roomId: string }) {
    context.commit('addToAttendeeDiscussionRooms', payload);
  },
  async loadPeerPrivateDiscussionRoom(context: AVContext, { eventId, userId }: any) {
    try {
      const eventID = eventId || context.rootState.route.params.eventID;
      const param = {
        event: eventID,
        owner: userId
      };
      let discussionRooms = [];
      if (userId) {
        const { data: discussionGroupsCached } = await loadDiscussionGroupP2P(param);
        discussionRooms = discussionGroupsCached as any;
      } else {
        const { data } = await loadDiscussionGroups(param);
        discussionRooms = data as any;
      }

      const room = (discussionRooms || []).map((d: any) => d.id)[0] ?? null;
      return room;
    } catch (e) {
      loggerService.error('Error in loadPeerPrivateDiscussionRoom');
    }
  },
  pinPost(context: AVContext, formattedChatMessage: FormattedChatMessage) {
    const chat = context.state.chats.find(it => it.channel.id === formattedChatMessage.channel_id);

    const message = chat?.channel?.messages?.find(message => message.id === formattedChatMessage.id);

    if (!message) {
      return;
    }

    message.is_pinned = true;

    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/posts/${formattedChatMessage.id}/pin`,
          method: 'POST'
        },
        { root: true }
      )
      .then(res => {
        message.serverResponseState = 'verified';
        message.error = undefined;
        return res;
      })
      .catch(err => {
        loggerService.error('error in pinPost', err);
        message.is_pinned = false;
        message.serverResponseState = 'error';
        message.error = 'pin_message_error';
      });
  },
  unpinPost(context: AVContext, formattedChatMessage: FormattedChatMessage) {
    const chat = context.state.chats.find(it => it.channel.id === formattedChatMessage.channel_id);

    const message = chat?.channel?.messages?.find(message => message.id === formattedChatMessage.id);

    const pinnedMessage = chat?.channel?.pinned_messages?.find(m => m.id === formattedChatMessage.id);

    if (message) {
      message.is_pinned = false;
    }

    if (pinnedMessage) {
      pinnedMessage.is_pinned = false;
    }

    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/posts/${formattedChatMessage.id}/unpin`,
          method: 'POST'
        },
        { root: true }
      )
      .then(res => {
        if (message) {
          message.serverResponseState = 'verified';
          message.error = undefined;
        }

        return res;
      })
      .catch(err => {
        loggerService.error('error in unpinPost', err);

        if (message) {
          message.is_pinned = true;
          message.serverResponseState = 'error';
          message.error = 'unpin_message_error';
        }

        if (pinnedMessage) {
          pinnedMessage.is_pinned = true;
          pinnedMessage.error = 'unpin_message_error';
          pinnedMessage.serverResponseState = 'error';
        }
      });
  },
  deletePost(context: AVContext, post_id: string) {
    return deletePost(context.rootState.event.event?.id, [post_id]);
  },
  deleteChatMessage(context: AVContext, formattedChatMessage: FormattedChatMessage) {
    const chat = context.state.chats.find(it => it.channel.id === formattedChatMessage.channel_id);

    const message = chat?.channel?.messages?.find(message => message.id === formattedChatMessage.id);

    if (!message) {
      return;
    }

    message.isMessageProcessing = true;

    context
      .dispatch('deletePost', formattedChatMessage.id)
      .catch(err => {
        loggerService.warn('error in deleteChatMessage', err);
        message.serverResponseState = 'error';
        message.error = 'delete_message_error';
      })
      .finally(() => {
        message.isMessageProcessing = false;
      });
  },
  pinnedMessages(context: AVContext, channel_id: string) {
    if (context.rootGetters['sidebar/isOverNinetyDays'] && context.rootState.event.useHideSidebar) {
      loggerService.info('on demand mode and over 90 days so not fetching pinned messages');
      return Promise.resolve([]);
    }
    return context
      .dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/channels/${channel_id}/pinned`,
          method: 'GET'
        },
        { root: true }
      )
      .then(res => {
        context.dispatch('addPinnedFormattedMessages', {
          res,
          channel_id
        });
      })
      .catch(err => {
        loggerService.warn('error in pinnedMessages', err);
      });
  },
  showLitemodeSwitchMessage(context: AVContext) {
    setTimeout(() => {
      context.state.liteModeSwitchMessage = i18n.global.t(
        '@@translate@@ Facing issues with audio or video? Switch on lite mode'
      );
      setTimeout(() => {
        context.state.liteModeSwitchMessage = '';
      }, 5000);
    }, 5000);
  },
  setHLSCaptionVisibility(context: AVContext, visible: boolean) {
    const player = context.rootState.simuliveroom.hlsVideoElement;
    const captionLanguage = <any>context?.rootState?.event?.stage?.captionLanguage;

    if (player && player.textTracks.length) {
      const track =
        player.textTracks &&
        Object.values(player.textTracks ?? {}).find((track: any) => track.language === captionLanguage);
      if (track) {
        track.mode = visible ? 'showing' : 'disabled';
      } else {
        player.textTracks[0].mode = visible ? 'showing' : 'disabled';
      }
    }
  },
  postSpeakerActivityNotification(
    context: AVContext,
    {
      activityType,
      message
    }: {
      activityType: ActivityType;
      message: string;
    }
  ) {
    const currentBroadcast = context.rootGetters['event/currentBroadcast'];
    if (!currentBroadcast?.speaker_chat_mmid) {
      return;
    }

    return context.dispatch('postMessage', {
      message,
      channel_id: currentBroadcast.speaker_chat_mmid,
      props: {
        kind: 'SpeakerActivityNotification',
        activityType
      }
    });
  }
};

export default actions;

function isOnDemandMqlEnabled(context: AVContext): boolean {
  return !!context.rootState.event?.event?.on_demand_mode && !!context.rootState.event?.useOnDemandMQL;
}

function handleOnDemandPollSubmitted(context: AVContext, currentPolledQuestion: PollQuestion): void {
  if (!isOnDemandMqlEnabled(context)) {
    return;
  }

  NotificationService.toast(
    i18n.global.t('@@translate@@ Success! Thank you for your response.'),
    '',
    'success',
    null,
    5000
  );
}

/* c8 ignore start */

function getOrderedFormattedMessages(order: string[], posts: any): FormattedChatMessage[] {
  return order.map((currentPostId: string, currentPostIndex: number) => {
    const currentPost = posts[currentPostId];
    const previousPostId = order[currentPostIndex - 1];

    let previousPost = null;
    if (previousPostId) {
      previousPost = posts[previousPostId];
    }

    return getFormattedChatMessage(currentPost, previousPost);
  });
}

export function getFormattedChatMessage(currentPost: any, previousPost?: any): FormattedChatMessage {
  const timeformatter = Intl.DateTimeFormat('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric'
  });

  const dateformatter = Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    weekday: 'short'
  });

  const createdAt = new Date(currentPost.create_at);

  let dayChange = false;
  if (previousPost) {
    dayChange = !datesAreOnSameDay(createdAt, new Date(previousPost.create_at));
  } else {
    dayChange = true;
  }

  const reactions: MessageReactions =
    currentPost.metadata?.reactions?.reduce((res: any, currentReaction: any) => {
      res[currentReaction.emoji_name] = [...(res[currentReaction.emoji_name] || []), currentReaction];
      return res;
    }, {}) || {};

  return {
    id: currentPost.id,
    dayChange,
    create_at: currentPost.create_at,
    update_at: currentPost.update_at,
    postedToday: datesAreOnSameDay(createdAt, new Date()),
    time: timeformatter.format(createdAt),
    day: dateformatter.format(createdAt),
    text: stripChatInputText(currentPost.message),
    is_pinned: currentPost.is_pinned,
    channel_id: currentPost.channel_id,
    reactions,
    isPostedAsQNA: !!currentPost?.props?.qna_post_id,
    gif: currentPost.props?.gif || undefined,
    error: currentPost.error,
    isMessageProcessing: currentPost.isMessageProcessing,
    serverResponseState: currentPost.serverResponseState,
    user_id: currentPost.user_id,
    root_id: currentPost.root_id,
    props: currentPost.props,
    type: currentPost.type,
    name: currentPost.identifiers?.name ?? 'name not found',
    mediumimage: currentPost.identifiers?.mediumimage ?? '',
    thumbnail: currentPost.identifiers?.thumbnail ?? '',
    is_in_person: currentPost.identifiers?.is_in_person ?? false,
    coreuserid: currentPost.identifiers?.coreuserid
  };
}

const debouncedGetters: Record<string, () => void> = {};

function makeDebouncedMessageGetter(context: AVContext, channel_id: string) {
  const bounceGetterIndex = channel_id;
  if (debouncedGetters[bounceGetterIndex]) {
    return debouncedGetters[bounceGetterIndex];
  }
  debouncedGetters[bounceGetterIndex] = debounce(() => {
    context.dispatch('fetchInitialMessagesNoLoader', { channel_id, fetchPinnedMessages: true });
  }, 15000);
  return debouncedGetters[bounceGetterIndex];
}

export const debounceForceMessagesUpdate = debounce(
  function () {
    // no code
  },
  100,
  {
    maxWait: 100,
    leading: true
  }
);

function makePollDetails(
  order: string[],
  postMap: Record<string, any>,
  currentUsersMMID: string,
  currentOpenedPollID: string = '',
  currentPublishedPollID: string = '',
  questions: Record<string, PollQuestion> = {},
  answerIDToQuestionIDMap: Record<string, string> = {}
): PollDetails {
  order.forEach(id => {
    const post = postMap[id];

    if (post.props?.type === 'QUESTION') {
      const doesQuestionExist = Object.prototype.hasOwnProperty.call(questions, id);
      if (!doesQuestionExist) {
        const question: PollQuestion = makeAnEmptyQuestion(id);
        questions[id] = question;
      }
      const question = questions[id];
      question.multipleChoice = post.props?.selectionType === 'MULTI';
      question.question = post.message || '';
      question.show_in_on_demand = post.props?.show_in_on_demand === true;
    } else if (post.props?.type === 'ANSWER') {
      const doesQuestionExist = Object.prototype.hasOwnProperty.call(questions, post.root_id);
      if (!doesQuestionExist) {
        const question: PollQuestion = makeAnEmptyQuestion(post.root_id);
        questions[id] = question;
      }

      const questionRef = questions[post.root_id];

      const answerIfAlreadyExists = questionRef.answers[post.id];
      const reactions = Array.isArray(post.metadata?.reactions) ? post.metadata.reactions : [];
      // When an admin edits an answer, the websocket event may not contain reaction data.
      // We must preserve the existing vote count to prevent the UI from showing 0.
      const currentAnsVotes = reactions.length || (answerIfAlreadyExists ? answerIfAlreadyExists.votes : 0);

      const answer: PollAnswer = {
        id: post.id || '',
        answer: post.message,
        votes: currentAnsVotes,
        loading: false
      };

      // Only update votedAnswers if we have definitive reaction data.
      // A post_edited event for an answer text change should not affect this.
      if (post.metadata?.reactions) {
        const votedByUser = reactions.some((reaction: MessageReaction) => {
          return reaction.user_id === currentUsersMMID;
        });
        const votedAnsIndex = questionRef.votedAnswers.indexOf(id);

        if (votedByUser && votedAnsIndex === -1) {
          questionRef.votedAnswers.push(id);
        } else if (!votedByUser && votedAnsIndex > -1) {
          questionRef.votedAnswers.splice(votedAnsIndex, 1);
        }
      }

      questionRef.answers[id] = answer;

      questionRef.totalVotes = Object.values(questionRef.answers).reduce(
        (total: number, ans: any) => total + ans.votes,
        0
      );
      answerIDToQuestionIDMap[id] = questionRef.id;
    } else if (post.props?.type === 'OPENED_POLL') {
      currentOpenedPollID = post.message;
    } else if (post.props?.type === 'PUBLISHED_POLL') {
      currentPublishedPollID = post.message;
    }
  });

  return {
    questions: questions,
    currentOpenedPollID: currentOpenedPollID,
    currentPublishedPollID: currentPublishedPollID,
    currentOnDemandOpenedPollIDs: [],
    answerIDToQuestionIDMap: answerIDToQuestionIDMap
  };
}

function makeAnEmptyQuestion(id: string): PollQuestion {
  return {
    id: id,
    answers: {},
    multipleChoice: false,
    question: '',
    show_in_on_demand: false,
    totalVotes: 0,
    votedAnswers: []
  };
}

function getAsyncPollPayload(
  context: AVContext,
  type: AsyncPollRequestDetails['type'],
  answer_id: string
): AsyncPollRequestDetails {
  return {
    type: type,
    payload: {
      channel_id: context.state.polls?.id || '',
      parent_post_id: context.state.polls?.details?.currentOpenedPollID || '',
      post_id: answer_id
    }
  };
}

function getAsyncPollsUrl(): string {
  return `${getEnv('VUE_APP_MM_SLOW_API') || ''}/poll`;
}

function isMattermostScalablityModeEnabled(event: EventState['event']) {
  const isUseExternalScalablityFlagSet = event?.scalability_flags?.use_external_apis;
  const isMattermostScalablityEnabledForEnv = getEnv('VUE_APP_ENABLE_MATTERMOST_SCALABLITY_MODE');
  return isUseExternalScalablityFlagSet || isMattermostScalablityEnabledForEnv;
}

/* c8 ignore stop */
